// Deliverables and their data points (SPEC-v2 D1-D3; API.md 7.6, endpoints 73-76).
import { z } from "zod";
import {
  DELIVERABLE_KINDS,
  DELIVERABLE_STATUSES,
  isDelivered,
  type ContractStatus,
  type DeliverableKind,
  type DeliverableStatus,
} from "../enums";
import { LIMITS } from "../limits";
import { httpUrlOrEmpty, isoDate, optionalText, requiredText, searchQuery, uuid } from "./common";
import { contributorsInput, type Contributor } from "./work";

// ---------- Requests ----------

export const dataPointInput = z
  .object({
    label: requiredText(LIMITS.dataPointLabel, "Label"),
    value: z
      .number({ invalid_type_error: "Enter a whole number." })
      .int("Enter a whole number.")
      .min(0, "Cannot be negative.")
      .max(LIMITS.dataPointValueMax, "The number is too large."),
    recordedOn: isoDate,
    note: optionalText(LIMITS.dataPointNote, "Note").default(""),
  })
  .strict();
export type DataPointInput = z.input<typeof dataPointInput>;

const dataPointsInput = z.array(dataPointInput).max(LIMITS.dataPointsMax, `At most ${LIMITS.dataPointsMax} data points.`);

const deliverableFields = {
  torId: uuid.nullable(),
  title: requiredText(LIMITS.deliverableTitle, "Title"),
  kind: z.enum(DELIVERABLE_KINDS),
  status: z.enum(DELIVERABLE_STATUSES),
  dueDate: isoDate.nullable(),
  deliveredOn: isoDate.nullable(),
  url: httpUrlOrEmpty(LIMITS.url),
  summary: optionalText(LIMITS.deliverableText, "Summary"),
  outcome: optionalText(LIMITS.deliverableText, "Outcome"),
};

/** 74. POST /api/deliverables. Status "delivered" without `deliveredOn` sets today (in the API). */
export const createDeliverableBody = z
  .object({
    contractId: uuid,
    torId: deliverableFields.torId.default(null),
    title: deliverableFields.title,
    kind: deliverableFields.kind,
    status: deliverableFields.status.default("planned"),
    dueDate: deliverableFields.dueDate.default(null),
    deliveredOn: deliverableFields.deliveredOn.default(null),
    url: deliverableFields.url.default(""),
    summary: deliverableFields.summary.default(""),
    outcome: deliverableFields.outcome.default(""),
    dataPoints: dataPointsInput.default([]),
    contributors: contributorsInput.default([]),
  })
  .strict();
export type CreateDeliverableBody = z.input<typeof createDeliverableBody>;
export type CreateDeliverableInput = z.output<typeof createDeliverableBody>;

/** 75. PATCH /api/deliverables/:id. `dataPoints` and `contributors` replace their whole lists. */
export const updateDeliverableBody = z
  .object({
    torId: deliverableFields.torId.optional(),
    title: deliverableFields.title.optional(),
    kind: deliverableFields.kind.optional(),
    status: deliverableFields.status.optional(),
    dueDate: deliverableFields.dueDate.optional(),
    deliveredOn: deliverableFields.deliveredOn.optional(),
    url: deliverableFields.url.optional(),
    summary: deliverableFields.summary.optional(),
    outcome: deliverableFields.outcome.optional(),
    dataPoints: dataPointsInput.optional(),
    contributors: contributorsInput.optional(),
  })
  .strict();
export type UpdateDeliverableBody = z.input<typeof updateDeliverableBody>;
export type UpdateDeliverableInput = z.output<typeof updateDeliverableBody>;

/** 73. GET /api/deliverables */
export const deliverableListQuery = z
  .object({
    contractId: uuid.optional(),
    status: z.enum([...DELIVERABLE_STATUSES, "overdue"]).optional(),
    torId: uuid.optional(),
    q: searchQuery.optional(),
  })
  .strict();
export type DeliverableListQuery = z.infer<typeof deliverableListQuery>;

// ---------- Responses ----------

export type DataPoint = { id: string; label: string; value: number; recordedOn: string; note: string };

export type Deliverable = {
  id: string;
  contract: { id: string; name: string; reference: string; status: ContractStatus };
  tor: { id: string; title: string; activityCode: string } | null;
  title: string;
  kind: DeliverableKind;
  status: DeliverableStatus;
  dueDate: string | null;
  deliveredOn: string | null;
  /** dueDate < today and status ≠ delivered */
  overdue: boolean;
  url: string;
  summary: string;
  outcome: string;
  /** by position */
  dataPoints: DataPoint[];
  contributors: Contributor[];
  createdAt: string;
  updatedAt: string;
};

/** "Overdue" (SPEC-v2 D1 AC2). `today` is "YYYY-MM-DD". */
export const isOverdue = (d: { dueDate: string | null; status: DeliverableStatus }, today: string): boolean =>
  d.dueDate !== null && d.dueDate < today && !isDelivered(d.status);
